import React, { useCallback, useEffect, useRef, useState, Suspense, lazy } from "react";
import { motion, MotionConfig, useReducedMotion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useCountry } from "../contexts/CountryContext";
import { getCountryContent } from "../data/countryContent";
import { getHomeContent } from "../data/homeContent";
import FAQAccordion from "../components/FAQAccordion";
import SectionInView from "../components/SectionInView";
import SEO from "../components/SEO";
import { BorderBeam } from "../components/BorderBeam";
import { usePageBgSync } from "../hooks/usePageBgSync";
import ClientOnly from "../components/ClientOnly";
import "../styles/button-gradients.css";

import EnhancedFooter from "../components/EnhancedFooter";
import { CountUp } from "../components/ui/count-up";

// Lazy load heavy / below-the-fold components for better performance
const FeaturesTimeline = lazy(() => import("../components/FeaturesTimeline"));
import {
  getSessionEmail,
  clearSessionEmail,
  clearSessionUserId,
  clearTierCookies,
} from "../lib/userSession";
import { setLocalhostAccessPreviewMode } from "../lib/adminAccess";
import { createPortal } from "react-dom";
import ComparisonTable from "../components/ComparisonTable";
import TrustBar from "../components/TrustBar";
import { submitDemoViewLead } from "../services/demoLeadService";
import { currentRegionHref } from "../lib/regionLocation";
import { canonicalUrl } from "../lib/region";

const LazyHeroBackground = lazy(() =>
  import("../components/HeroBackground").then((mod) => ({
    default: mod.default,
  }))
);
const LazyDitherShader = lazy(() =>
  import("../components/DitherShader").then((mod) => ({
    default: mod.DitherShader || mod.default,
  }))
);

/** The Singapore walkthrough; a site's own video comes from its content (`home.demo.video`). */
const DEFAULT_DEMO_VIDEO_SRC = "/localfiles/Assessment_Demo_2026.mp4";

/** The sheet theme (src/lib/siteTheme.js, the live default) hides the hero particle canvas, so it is not mounted at all there. */
function isSheetTheme() {
  if (typeof document === "undefined") return false;
  return document.documentElement.getAttribute("data-theme") === "sheet";
}

/** Rendered next to a lazy component inside its Suspense: fires once that chunk has mounted. */
function MountSignal({ onMount }) {
  useEffect(() => {
    onMount();
  }, [onMount]);
  return null;
}

const HERO_STAT_SLOT_CLASS = "flex min-w-0 items-start justify-center py-3 sm:py-4 lg:justify-start";
const HERO_STAT_NUMBER_CLASS = "font-bold text-[28px] sm:text-[32px] leading-none";
const HERO_STAT_SUFFIX_CLASS = "font-bold text-[18px] sm:text-[20px] leading-none";
const HERO_STAT_ACCENT_STYLE = { color: '#60A5FA', lineHeight: 1 };
const HERO_STAT_LABEL_STYLE = {
  color: '#64748B',
  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: 1.4,
};
const US_SETUP_FEE_PHRASE = "one-time $200 setup fee";
const US_ACCENT_BLUE = "#2563eb";

function emphasiseUsSetupFee(text, isUS) {
  if (!isUS || typeof text !== "string" || !text.includes(US_SETUP_FEE_PHRASE)) return text;

  const [before, ...afterParts] = text.split(US_SETUP_FEE_PHRASE);
  return (
    <>
      {before}
      <strong style={{ color: US_ACCENT_BLUE, fontWeight: 700 }}>{US_SETUP_FEE_PHRASE}</strong>
      {afterParts.join(US_SETUP_FEE_PHRASE)}
    </>
  );
}

/** One slot of the hero stats strip. The figures and labels are per-country (src/data/homeContent.js). */
function HeroStat({ stat, className, reducedMotion }) {
  return (
    <div className={className}>
      <div className="flex min-w-0 flex-col items-center gap-1 lg:items-start">
        <div className="flex items-baseline gap-1">
          {stat.animate && !reducedMotion ? (
            <CountUp
              from={0}
              to={stat.value}
              duration={3}
              className={HERO_STAT_NUMBER_CLASS}
              style={HERO_STAT_ACCENT_STYLE}
            />
          ) : (
            <span className={HERO_STAT_NUMBER_CLASS} style={HERO_STAT_ACCENT_STYLE}>
              {stat.value}
            </span>
          )}
          {stat.suffix ? (
            <span className={HERO_STAT_SUFFIX_CLASS} style={HERO_STAT_ACCENT_STYLE}>
              {stat.suffix}
            </span>
          ) : null}
        </div>
        <span className="hero-stat-label block max-w-[15rem] break-words" style={HERO_STAT_LABEL_STYLE}>
          {[stat.label[0], stat.label[1]].filter(Boolean).join(' ')}
        </span>
      </div>
    </div>
  );
}

/**
 * HomePage - Main homepage component matching the reference image
 * Features: New hero section with ribbons, logo, and navigation
 * Preserves all existing content below the hero section
 */
export default function HomePage() {
  const location = useLocation();
  const { country } = useCountry();
  const countryContent = getCountryContent(country);
  // Everything country-specific on this page (copy, hero photo, stats, product links) comes from here.
  const home = getHomeContent(country);
  const isUSHome = home.region === "us";
  const heroImage = home.hero.image;
  const heroDitherPalette = heroImage.ditherPalette || ["#6fa8fa", "#9fc5fb", "#e5efff", "#FFFFFF", "#FFFFFF"];
  const heroImageA11y = heroImage.alt ? { role: "img", "aria-label": heroImage.alt } : {};
  const logoRef = useRef(null);
  // framer-motion's own reduced-motion signal; MotionConfig below also stops every variant/whileInView.
  const prefersReducedMotion = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const [heroDitherReady, setHeroDitherReady] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [demoStep, setDemoStep] = useState("email");
  const [demoEmail, setDemoEmail] = useState(() => getSessionEmail() || "");
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoError, setDemoError] = useState("");
  const heroRef = useRef(null);
  const demoModalRef = useRef(null);
  const demoTriggerRef = useRef(null);
  const [featuresReady, setFeaturesReady] = useState(false);
  const markFeaturesReady = useCallback(() => setFeaturesReady(true), []);
  const MotionDiv = motion.div;
  const heroLede = home.hero.lede;
  const assessmentCard = home.pricing.assessment;
  const supportCard = home.pricing.support;
  // U.S.: three priced tiers instead of the single assessment card (src/data/homeContent.js).
  const pricingTiers = home.pricing.tiers || null;

  const scrollToHeroCtaAnchor = (event) => {
    const target = document.getElementById(home.hero.cta.anchor);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Same behavior as the header's Speak With Us: Cal.com modal when the
  // embed is loaded, new-tab fallback otherwise.
  const handleHeroSpeakWithUs = () => {
    if (typeof window === 'undefined') return;
    try {
      const calNs = window.Cal?.ns?.['15min'];
      if (typeof calNs === 'function') {
        calNs('modal', { calLink: 'optralabs/15min' });
        return;
      }
    } catch {
      void 0;
    }
    window.open('https://cal.com/optralabs/15min', '_blank', 'noopener,noreferrer');
  };

  // The landing page is always a signed-out surface: anyone arriving here is
  // logged out (Clerk + local session state), so the dashboard is only
  // reachable through a fresh Sign In. Applies to everyone, admins included.
  // (Singapore product only. The US product is a separate app that shares the Clerk session and
  // links back to /us while signed in, so its pack turns this off: home.product.signOutOnLanding.)
  const signOutOnLanding = home.product.signOutOnLanding;
  useEffect(() => {
    if (!signOutOnLanding) return undefined;
    try {
      setLocalhostAccessPreviewMode(null);
      clearSessionEmail();
      clearSessionUserId();
      clearTierCookies();
    } catch {
      // non-fatal
    }
    let cancelled = false;
    let attempts = 0;
    // Clerk loads asynchronously; retry briefly so a signed-in session is
    // ended even when the script arrives after mount.
    const trySignOut = () => {
      if (cancelled) return;
      try {
        const clerk = typeof window !== "undefined" ? window.Clerk : null;
        if (clerk?.user && typeof clerk.signOut === "function") {
          void clerk.signOut().catch(() => {});
          return;
        }
        if (clerk?.loaded) return; // loaded and no user: nothing to do
      } catch {
        // non-fatal
      }
      attempts += 1;
      if (attempts < 20) window.setTimeout(trySignOut, 500);
    };
    trySignOut();
    return () => {
      cancelled = true;
    };
  }, [signOutOnLanding]);


  // Check if component is mounted (client-side only)
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  // Enable seamless background synchronization
  usePageBgSync(true);
  
  // const prefetchChecker = () => { // Removed unused function
  //   void import('./CheckerPage.jsx');
  //   if ('requestIdleCallback' in window) {
  //     requestIdleCallback(() => { void import('./CheckerPage.jsx'); });
  //   }
  // };

  // Demo dialog: lock scroll, close on Escape, keep Tab inside the dialog, move focus in on open
  // and back to the "View Demo" button on close.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!isDemoModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsDemoModalOpen(false);
        return;
      }
      if (event.key !== "Tab" || !demoModalRef.current) return;
      const focusable = Array.from(demoModalRef.current.querySelectorAll(focusableSelector));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    const target = demoModalRef.current?.querySelector("#demo-email-input, video, button");
    if (target && typeof target.focus === "function") target.focus();
    // The "View Demo" button that opened the dialog; focus goes back to it on close.
    const trigger = demoTriggerRef.current;

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      if (trigger && typeof trigger.focus === "function") trigger.focus();
    };
  }, [isDemoModalOpen]);

  useEffect(() => {
    const hash = window.location.hash || '';
    // "/pricing" is this page scrolled to its pricing section (a route of the US site).
    const anchor = hash || (location?.pathname === '/pricing' ? '#pricing' : '');

    // Preserve anchor navigation for header links (e.g. #ask-optra, #pricing).
    // We only force scroll-to-top when there is no hash target.
    if (anchor) {
      const sectionId = anchor.replace(/^#/, '').trim();
      if (sectionId) {
        const scrollToHashTarget = (attempt = 0) => {
          const target = document.getElementById(sectionId);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
          if (attempt < 12) {
            setTimeout(() => scrollToHashTarget(attempt + 1), 100);
          }
        };
        setTimeout(() => scrollToHashTarget(0), 80);
      }
    } else {
      // No hash: regular page entry should start from top.
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    // Backward compatibility: wizard deep links → the dashboard assessment
    if (hash.startsWith('#step-') || hash === '#wizard-start') {
      // history / window.location bypass React Router, so the region prefix is added here.
      // (The US product is a separate app: home.product.href() already returns its full path.)
      const next = currentRegionHref(home.product.href('/dashboard/eligibility-assessment' + hash));
      window.history.replaceState({}, '', next);
      window.location.assign(next);
      return;
    }
    
    if (location?.state && typeof location.state.focusSection === 'number') {
      const idx = location.state.focusSection;
      setTimeout(() => {
        scrollToSection(idx);
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on navigation only, as before
  }, [location]);

  // The features timeline (above #pricing) is a lazy chunk: when it lands the page grows and a
  // viewport that was scrolled to an anchor (/pricing, #pricing, #faq-section) drifts. Re-align once.
  const pathname = location?.pathname;
  useEffect(() => {
    if (!featuresReady || typeof window === "undefined") return;
    const anchor = window.location.hash || (pathname === "/pricing" ? "#pricing" : "");
    const target = anchor ? document.getElementById(anchor.replace(/^#/, "")) : null;
    if (target) target.scrollIntoView({ behavior: "auto", block: "start" });
  }, [featuresReady, pathname]);

  const scrollToSection = (idx) => {
    const sectionsEl = sectionRefs.current;
    if (sectionsEl[idx]) {
      sectionsEl[idx].scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sectionRefs = useRef([]);

  const openDemoModal = () => {
    setDemoEmail(getSessionEmail() || "");
    setDemoError("");
    setDemoSubmitting(false);
    setDemoStep("email");
    setIsDemoModalOpen(true);
  };

  const closeDemoModal = () => {
    setIsDemoModalOpen(false);
    setDemoSubmitting(false);
    setDemoError("");
    setDemoStep("email");
  };

  const handleDemoLeadSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = demoEmail.trim().toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(normalizedEmail)) {
      setDemoError("Please enter a valid work email.");
      return;
    }

    setDemoSubmitting(true);
    setDemoError("");

    const result = await submitDemoViewLead({
      email: normalizedEmail,
      pagePath: home.demo.leadPagePath,
      demoAsset: (home.demo.video || DEFAULT_DEMO_VIDEO_SRC).split("/").pop(),
    });

    if (result.error && import.meta.env.DEV) {
      console.warn("[View Demo] Lead capture did not persist:", result.error);
    }

    setDemoStep("video");
    setDemoSubmitting(false);
  };

  // JSON-LD structured data for SEO
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://www.optra-labs.com/#organization",
      "name": "OPTRA Labs",
      "description": heroLede,
      "url": "https://www.optra-labs.com",
      "logo": "https://www.optra-labs.com/favicon.png",
      "foundingDate": "2024",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": countryContent.jsonLd.addressCountry,
        "addressLocality": countryContent.jsonLd.addressLocality
      },
      "sameAs": [
        countryContent.jsonLd.linkedInUrl,
        "https://www.instagram.com/optra.labs/"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      // One WebSite node per regional site: /sg for Singapore, /us for the United States.
      "@id": `${canonicalUrl("/", home.region)}#website`,
      "url": canonicalUrl("/", home.region),
      "name": "OPTRA Labs",
      "description": heroLede,
      "publisher": {
        "@id": "https://www.optra-labs.com/#organization"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "OPTRA Labs Eligibility Assessment",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": heroLede,
      // One Offer per plan when the pack lists them (U.S. tiers: one-time setup fee, then the tier's
      // monthly price), otherwise the single offer (Singapore: no amount, it lives in Stripe).
      "offers": home.pricing.offers
        ? home.pricing.offers.map((offer) => ({
            "@type": "Offer",
            "name": offer.name,
            "description": offer.description,
            "price": offer.price,
            "priceCurrency": countryContent.currency,
            "priceSpecification": offer.priceSpecification.map((spec) => ({
              ...spec,
              "priceCurrency": countryContent.currency
            }))
          }))
        : {
        "@type": "Offer",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": countryContent.currency,
          "description": home.pricing.offerDescription
        }
      },
      "provider": {
        "@id": "https://www.optra-labs.com/#organization"
      }
    }
  ];

  // Keep native scrolling on the browser's fast path.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const root = document.documentElement;
    const body = document.body;
    const previous = {
      rootScrollSnapType: root.style.scrollSnapType,
      bodyScrollSnapType: body.style.scrollSnapType,
      rootScrollBehavior: root.style.scrollBehavior,
      bodyScrollBehavior: body.style.scrollBehavior,
      rootWebkitOverflowScrolling: root.style.webkitOverflowScrolling,
      bodyWebkitOverflowScrolling: body.style.webkitOverflowScrolling,
    };

    root.style.scrollSnapType = 'none';
    body.style.scrollSnapType = 'none';
    root.style.scrollBehavior = 'smooth';
    body.style.scrollBehavior = 'smooth';

    if (window.matchMedia?.('(pointer: coarse)').matches) {
      root.style.webkitOverflowScrolling = 'touch';
      body.style.webkitOverflowScrolling = 'touch';
    }

    return () => {
      root.style.scrollSnapType = previous.rootScrollSnapType;
      body.style.scrollSnapType = previous.bodyScrollSnapType;
      root.style.scrollBehavior = previous.rootScrollBehavior;
      body.style.scrollBehavior = previous.bodyScrollBehavior;
      root.style.webkitOverflowScrolling = previous.rootWebkitOverflowScrolling;
      body.style.webkitOverflowScrolling = previous.bodyWebkitOverflowScrolling;
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <div
      className="home-page min-h-screen w-full max-w-full relative md:min-h-screen"
      style={{ margin: 0, padding: 0, left: 0, right: 0 }}
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <SEO
        title={home.seo.title}
        description={heroLede}
        keywords={home.seo.keywords}
        url="/"
      />

      {/* Silk Background Animation - NOW GLOBAL IN APP.JSX */}
      {/* No static background div - Silk provides animated background */}

      {/* Header is now rendered at App level for proper sticky positioning */}

      <section
        id="hero-section"
        ref={heroRef}
        className="relative min-h-screen flex items-center w-full"
        style={{
          // The page wrapper already offsets the fixed navbar, so the hero fills the rest of the
          // first screen (viewport minus header) and centres its content in it. Symmetric padding
          // keeps that centre true; on short viewports the section simply grows with its content.
          paddingTop: 'clamp(1.5rem, 3vh, 2.5rem)',
          paddingBottom: 'clamp(1.5rem, 3vh, 2.5rem)',
          minHeight: 'calc(100dvh - var(--header-live-h, var(--nav-h, 80px)))',
          width: '100%',
          position: 'relative',
          overflow: 'clip',
          backgroundColor: '#FFFFFF',
        }}
      >
        {!isSheetTheme() && (
          <ClientOnly>
            <Suspense fallback={null}>
              <LazyHeroBackground />
            </Suspense>
          </ClientOnly>
        )}

        {/* Hero-only grid pattern overlay (subtle) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 2,
            backgroundImage:
              "linear-gradient(rgba(96,165,250,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.055) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
            backgroundPosition: "center center",
          }}
        />

        {/* Fade grid/background to white toward trust bar */}
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            zIndex: 3,
            height: "12%",
            background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,1) 100%)",
          }}
        />
        
        <div 
          className="relative z-10 w-full"
        >
          {/* Two-column layout: text left, artwork right - grouped and lifted slightly */}
          <div 
            className="hero-columns flex flex-col lg:flex-row items-start lg:items-center justify-between w-full max-w-7xl mx-auto px-4 md:px-12 lg:px-20"
            style={{
              gap: 'clamp(1.5rem, 4vw, 4rem)',
            }}
          >
            {/* Left: Text Content */}
            <div 
              id="hero-group" 
              ref={logoRef}
              className="flex min-w-0 flex-col w-full items-center justify-center text-center lg:w-auto lg:flex-1 lg:items-start lg:text-left"
              style={{
                maxWidth: '600px',
                gap: '1.25rem',
              }}
            >
            <h1
              className="text-emphasis leading-[1.08]"
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 'clamp(1.7425rem, 1.05rem + 2.9vw, 3.1875rem)',
                letterSpacing: '-0.022em',
                color: '#1F2937',
                margin: 0,
                fontWeight: 400,
                maxWidth: '520px',
              }}
            >
              <span>Predictive Intelligence</span>
              <br />
              <span>For Winning The</span>
              <br />
              <motion.span
                initial={{ opacity: 0.9, y: 4 }}
                animate={{ opacity: 1, y: [4, -2, 0] }}
                transition={{ duration: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
                className="inline-block rounded-md px-2"
                style={{
                  color: "#60A5FA",
                  background: "linear-gradient(90deg, rgba(147,197,253,0.28), rgba(96,165,250,0.12))",
                }}
              >
                Right Funding
              </motion.span>
            </h1>

            {/* Mobile hero artwork: keep desktop placement unchanged */}
            <div
              className="lg:hidden w-full overflow-hidden rounded-sm"
              style={{
                maxWidth: '520px',
                height: '220px',
                backgroundColor: '#DBEAFE',
                opacity: heroDitherReady ? 1 : 0,
                transition: 'opacity 180ms ease-out',
              }}
            >
              <div
                className="relative w-full h-full overflow-hidden"
                style={{ isolation: 'isolate' }}
              >
                <div className="absolute inset-0 w-full h-full overflow-hidden" style={{ zIndex: 1 }} {...heroImageA11y}>
                  <LazyDitherShader
                    src={heroImage.src}
                    colorMode="custom"
                    customPalette={heroDitherPalette}
                    gridSize={2}
                    pixelRatio={1}
                    ditherMode="bayer"
                    objectFit="cover"
                    objectPosition={heroImage.objectPosition}
                    threshold={0.44}
                    onReady={() => setHeroDitherReady(true)}
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              </div>
            </div>

            <p
              className="text-ui optra-description-text text-sm sm:text-base md:text-lg leading-[1.7]"
              style={{
                color: '#64748B',
                margin: 0,
                fontWeight: 300,
                letterSpacing: '0.003em',
                maxWidth: '520px'
              }}
            >
              {heroLede}
            </p>

            {/* Primary: the assessment (the page's one conversion). Secondary: the demo video and,
                on phones only, the Speak With Us call that the header hides there. */}
            <div
              className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 w-full sm:w-auto"
              style={{
                marginTop: '0.25rem',
                marginBottom: '0.5rem',
                maxWidth: '520px',
              }}
            >
              <a
                // A pack either links out (`href`) or names a section of this page (`anchor`, the
                // U.S. pricing tiers): that one scrolls in place, like the testimonial's #pricing link.
                href={home.hero.cta.anchor ? currentRegionHref(`/#${home.hero.cta.anchor}`, home.region) : home.hero.cta.href}
                {...(home.hero.cta.anchor ? { onClick: scrollToHeroCtaAnchor } : {})}
                {...(home.hero.cta.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="text-ui inline-flex items-center justify-center w-full sm:w-auto px-7 py-3 rounded-lg text-sm font-medium transition-all duration-150 min-h-[48px]"
                style={{
                  color: '#FFFFFF',
                  backgroundColor: '#60A5FA',
                  border: '1px solid #60A5FA',
                  cursor: 'pointer',
                  fontWeight: 500,
                  boxShadow: 'none',
                  textDecoration: 'none',
                }}
              >
                Start Your Assessment
                {home.hero.cta.srHint ? <span className="sr-only"> ({home.hero.cta.srHint})</span> : null}
              </a>
              <button
                type="button"
                ref={demoTriggerRef}
                onClick={openDemoModal}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(111, 168, 250, 0.5)';
                  e.currentTarget.style.color = '#6789dc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(111, 168, 250, 0.3)';
                  e.currentTarget.style.color = '#4B5563';
                }}
                className="text-ui w-full sm:w-auto px-7 py-3 rounded-lg text-sm font-normal transition-all duration-150 min-h-[48px]"
                style={{
                  color: '#4B5563',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(111, 168, 250, 0.3)',
                  cursor: 'pointer',
                  fontWeight: 400
                }}
              >
                View Demo
              </button>

              {/* Mobile: same outlined style, but books a call instead */}
              <button
                type="button"
                onClick={handleHeroSpeakWithUs}
                className="md:hidden! text-ui w-full sm:w-auto px-7 py-3 rounded-lg text-sm font-normal transition-all duration-150 min-h-[48px]"
                style={{
                  color: '#4B5563',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(111, 168, 250, 0.3)',
                  cursor: 'pointer',
                  fontWeight: 400
                }}
              >
                Speak With Us
              </button>
            </div>
            {/* Hero stats strip - top separator only; point text largest */}
            <div
              className="mt-0 sm:mt-0.5 grid w-full"
              style={{
                maxWidth: '640px',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                columnGap: 'clamp(0.75rem, 2vw, 1.5rem)',
              }}
            >
              {home.hero.stats.map((stat, index) => (
                <HeroStat
                  key={index}
                  stat={stat}
                  className={HERO_STAT_SLOT_CLASS}
                  reducedMotion={prefersReducedMotion}
                />
              ))}
            </div>
            </div>

            {/* Right: Hero image card; hidden on mobile */}
            <div 
              className="hidden lg:flex flex-shrink-0 self-center overflow-hidden rounded-[14px]"
              style={{
                width: 'clamp(320px, 34vw, 460px)',
                aspectRatio: '460 / 528',
                height: 'auto',
                backgroundColor: '#DBEAFE',
                boxShadow: '0 24px 56px rgba(15,23,42,0.14), 0 8px 22px rgba(15,23,42,0.08)',
                opacity: heroDitherReady ? 1 : 0,
                transition: 'opacity 180ms ease-out',
              }}
            >
              <div
                className="relative w-full h-full overflow-hidden"
                style={{ isolation: 'isolate' }}
              >
                <div className="absolute inset-0 w-full h-full overflow-hidden" style={{ zIndex: 1 }} {...heroImageA11y}>
                  <LazyDitherShader
                    src={heroImage.src}
                    colorMode="custom"
                    customPalette={heroDitherPalette}
                    gridSize={2}
                    pixelRatio={1}
                    ditherMode="bayer"
                    objectFit="cover"
                    objectPosition={heroImage.objectPosition}
                    threshold={0.44}
                    onReady={() => setHeroDitherReady(true)}
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* ========== ALL SECTIONS (Silk background is fixed at page level) ========== */}
      <div className="relative flex flex-col">
        {/* Trust Bar */}
        <TrustBar />

        {/* Testimonial */}
        <section className="relative z-20 w-full px-4 md:px-6 pt-16 md:pt-20 pb-12 md:pb-16">
          <MotionDiv
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto w-full max-w-6xl"
          >
            <div className="flex flex-col items-center gap-8 sm:gap-10 text-center">
              <div className="flex flex-col items-center gap-2">
                <span
                  className="inline-flex items-center text-ui text-[10px] uppercase px-2.5 py-1 w-fit"
                  style={{
                    color: "#60A5FA",
                    backgroundColor: "#FFFFFF",
                    fontWeight: 500,
                    letterSpacing: "0.08em",
                    borderRadius: "8px",
                    border: "1px solid rgba(96, 165, 250, 0.28)",
                  }}
                  >
                    Client perspectives
                  </span>
              </div>

              <div className="flex flex-col items-center gap-6 sm:gap-8">
                <blockquote
                  className="text-ui w-full max-w-4xl mx-auto"
                  style={{
                    color: "#475569",
                    fontWeight: 300,
                    fontSize: "clamp(1.05rem, 2.4vw, 1.45rem)",
                    lineHeight: 1.32,
                    letterSpacing: "0.0012em",
                    margin: 0,
                  }}
                  >
                  <span style={{ display: "block" }}>
                    <span
                      aria-hidden="true"
                      style={{
                        color: "#BFDBFE",
                        fontFamily: "Georgia, serif",
                        fontSize: "1em",
                        lineHeight: 1,
                        position: "static",
                    }}
                  >
                    “
                  </span>
                  As an SME owner, OPTRA Labs’{" "}
                  <a
                    // index.html sets <base href="/">, so a bare "#pricing" would resolve to the
                    // region chooser. Point at this region's homepage and scroll in place.
                    href={currentRegionHref("/#pricing")}
                    onClick={(event) => {
                      const pricingSection = document.getElementById("pricing");
                      if (!pricingSection) return;
                      event.preventDefault();
                      pricingSection.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="testimonial-inline-link transition-all duration-200 hover:underline hover:underline-offset-4"
                    data-inline-link=""
                    style={{ color: "#1d4ed8", fontWeight: "inherit", fontSize: "inherit", fontFamily: "inherit" }}
                  >
                    Grant Eligibility Assessment
                  </a>{" "}
                  has saved me considerable time and given me peace of mind that we didn’t miss potential grants we were eligible for.
                    <span
                      aria-hidden="true"
                      style={{
                        color: "#BFDBFE",
                        fontFamily: "Georgia, serif",
                        fontSize: "1em",
                        lineHeight: 1,
                        position: "static",
                      }}
                    >
                      ”
                    </span>
                  </span>
                </blockquote>
                <div className="flex flex-col items-center gap-1.5 pt-0.5">
                  <span
                    className="text-ui text-[0.84rem] sm:text-[0.9rem] tracking-[0.04em]"
                    style={{ color: "#60A5FA", fontWeight: 600, fontFamily: "'Georgia Pro', Georgia, serif" }}
                  >
                    Olivier Too
                  </span>
                  <a
                    href="https://auptimate.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ui text-[0.9rem] sm:text-[0.98rem] transition-all duration-200 hover:underline hover:underline-offset-4"
                    style={{ color: "#64748B", fontWeight: 300 }}
                  >
                    Auptimate, Chief Executive Officer
                  </a>
                  <div className="flex items-center gap-0.5" aria-label="Five star rating">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span
                        key={`testimonial-star-${index}`}
                        className="text-[0.95rem] sm:text-[1rem]"
                        style={{ color: "#93C5FD", lineHeight: 1 }}
                        aria-hidden="true"
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </MotionDiv>
        </section>

        {/* Features Timeline. Sections follow DOM order on every viewport: features, pricing, comparison. */}
        <div className="relative z-20">
          <Suspense fallback={null}>
            <FeaturesTimeline />
            <MountSignal onMount={markFeaturesReady} />
          </Suspense>
        </div>

        {/* Pricing */}
        <section id="pricing" className="section-standard bg-transparent max-w-6xl mx-auto px-4 md:px-6 scroll-mt-24 relative z-20">
          <SectionInView id="waitlist" />
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-4">
              <span
                className="inline-flex items-center text-ui text-[10px] uppercase px-2.5 py-1"
                style={{
                  color: "#60A5FA",
                  backgroundColor: "#FFFFFF",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  borderRadius: "8px",
                  border: "1px solid rgba(96, 165, 250, 0.35)",
                }}
              >
                Service options
              </span>
            </div>
            <h2
              id="pricing-heading"
              className="scroll-mt-24 text-emphasis text-[1.7rem] sm:text-[1.59375rem] md:text-[3.1875rem] lg:text-[3.1875rem] leading-[1.08] text-center mb-4"
              style={{
                letterSpacing: "-0.022em",
                color: "#60A5FA",
                margin: 0,
                marginBottom: "1.75rem",
                fontWeight: 400,
              }}
            >
              Unlock the Right Funding
            </h2>
            <p className="text-ui optra-description-text text-sm sm:text-xl text-center mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: '#4B5563', fontWeight: 300 }}>
              Discover grants and other funding paths that fit your business, then move faster from eligibility to application with clear next steps.
            </p>
          </MotionDiv>
          {pricingTiers ? (
            <>
              {/* U.S.: pay once for a tier (covers the first month), then the same monthly price on every tier. */}
              <p
                className="pricing-note text-ui text-sm text-center max-w-3xl mx-auto mb-8 px-4 py-3 leading-relaxed"
                style={{
                  color: "#1F2937",
                  fontWeight: 400,
                  border: "1px solid rgba(96, 165, 250, 0.35)",
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {emphasiseUsSetupFee(home.pricing.note, isUSHome)}
              </p>
              <div className="pricing-cards pricing-tiers grid gap-6 lg:grid-cols-3 max-w-6xl mx-auto items-stretch px-1 sm:px-2">
                {pricingTiers.map((tier, index) => {
                  const cta = tier.cta;
                  return (
                    <MotionDiv
                      key={tier.kicker}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.5, delay: 0.05 * (index + 1) }}
                      className="rounded-2xl border border-[#DCE8FB] bg-white/92 backdrop-blur-xl shadow-[0_20px_48px_rgba(96,165,250,0.16)] p-5 sm:p-6 md:p-8 flex flex-col min-h-[560px] transition-transform duration-200 hover:-translate-y-0.5"
                    >
                      <span
                        className="inline-flex items-center w-fit text-ui text-[10px] uppercase mb-4 px-2.5 py-1"
                        style={{
                          color: "#60A5FA",
                          backgroundColor: "#FFFFFF",
                          fontWeight: 500,
                          letterSpacing: "0.08em",
                          borderRadius: "8px",
                          border: "1px solid rgba(96, 165, 250, 0.35)",
                          boxShadow: "0 0 0 1px rgba(96, 165, 250, 0.08), 0 4px 12px rgba(96, 165, 250, 0.12)",
                        }}
                      >
                        {tier.kicker}
                      </span>
                      <h3 className="text-emphasis text-[1.59375rem] md:text-[1.8275rem] leading-[1.15] mb-6" style={{ color: "#60A5FA", fontWeight: 400 }}>
                        {tier.title}
                      </h3>
                      <p className="text-ui text-sm md:text-xs mb-5" style={{ color: "#6B7280", fontWeight: 300 }}>
                        {tier.tagline}
                      </p>
                      {/* Price: the tier's monthly amount, then the one-time setup fee that covers the first month. */}
                      <div className="pricing-price mb-5">
                        <p
                          className="pricing-price-amount text-emphasis leading-none"
                          style={{ color: "#60A5FA", fontWeight: 400, fontSize: "2.75rem", letterSpacing: "-0.02em", margin: 0 }}
                        >
                          {tier.price.amount}
                        </p>
                        <p className="pricing-price-note text-ui text-sm" style={{ color: "#1F2937", fontWeight: 400, margin: "0.625rem 0 0" }}>
                          {tier.price.note}
                        </p>
                        <p className="pricing-price-recurring text-ui text-sm" style={{ color: "#6B7280", fontWeight: 300, margin: "0.125rem 0 0" }}>
                          {emphasiseUsSetupFee(tier.price.recurring, isUSHome)}
                        </p>
                      </div>
                      <div className="mb-7 mt-2" style={{ borderTop: "1px solid rgba(148, 163, 184, 0.25)" }} />
                      <div className="flex flex-wrap gap-2 mb-6">
                        {tier.tags.map((tag) => (
                          <span key={tag} className="text-ui text-[10px] uppercase px-2 py-1 rounded-md" style={{ color: "#0C447C", backgroundColor: "#EAF2FF", fontWeight: 500, letterSpacing: "0.06em" }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p className="text-ui optra-description-text text-sm md:text-lg mb-3 leading-relaxed" style={{ color: "#1F2937", fontWeight: 400 }}>
                        {tier.lead}
                      </p>
                      <p className="text-ui optra-description-text text-sm mb-6 leading-relaxed" style={{ color: "#6B7280", fontWeight: 300 }}>
                        {tier.body}
                      </p>
                      <ul className="space-y-3 mb-8">
                        {tier.bullets.map((item, idx) => (
                          <li key={idx} className="text-ui text-sm md:text-base flex items-start gap-2" style={{ color: "#4B5563", fontWeight: 300 }}>
                            <span className="mt-0.5 flex-shrink-0" style={{ color: "#60A5FA" }}>✓</span>
                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                      {/* Stripe Payment Link in a new tab when configured, else the U.S. sign-in (src/regions/us/checkout.js). */}
                      <a
                        href={cta.href}
                        data-pricing-cta={`tier-${index + 1}`}
                        {...(cta.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="group mt-auto inline-flex items-center justify-center gap-2 w-full text-ui px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-0.5"
                        style={{
                          color: "#FFFFFF",
                          background: "#60A5FA",
                          border: "1px solid #60A5FA",
                          cursor: "pointer",
                          fontWeight: 500,
                          boxShadow: "0 8px 20px rgba(96, 165, 250, 0.26)",
                        }}
                      >
                        {tier.ctaLabel}
                        {cta.newTab ? <span className="sr-only"> (opens secure Stripe checkout in a new tab)</span> : null}
                      </a>
                    </MotionDiv>
                  );
                })}
              </div>
              {/* How upgrades are priced: the higher monthly price at once, prorated for the rest of the month. */}
              <p
                className="pricing-upgrade text-ui text-sm text-center max-w-3xl mx-auto leading-relaxed"
                style={{ color: "#4B5563", fontWeight: 300, margin: "1.5rem auto 0" }}
              >
                {home.pricing.upgradeNote}
              </p>
            </>
          ) : (
          <div className="pricing-cards grid gap-6 lg:grid-cols-2 max-w-6xl mx-auto items-stretch px-1 sm:px-2">
            {/* Card 1: the self-serve assessment. Copy, tags, bullets and the CTA target are per country. */}
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="rounded-2xl border border-[#DCE8FB] bg-white/92 backdrop-blur-xl shadow-[0_20px_48px_rgba(96,165,250,0.16)] p-5 sm:p-6 md:p-8 flex flex-col min-h-[560px] transition-transform duration-200 hover:-translate-y-0.5"
            >
              <span
                className="inline-flex items-center w-fit text-ui text-[10px] uppercase mb-4 px-2.5 py-1"
                style={{
                  color: "#60A5FA",
                  backgroundColor: "#FFFFFF",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  borderRadius: "8px",
                  border: "1px solid rgba(96, 165, 250, 0.35)",
                  boxShadow: "0 0 0 1px rgba(96, 165, 250, 0.08), 0 4px 12px rgba(96, 165, 250, 0.12)",
                }}
              >
                {assessmentCard.kicker}
              </span>
              <h3 className="text-emphasis text-[1.59375rem] md:text-[1.8275rem] leading-[1.15] mb-6" style={{ color: "#60A5FA", fontWeight: 400 }}>
                {assessmentCard.title}
              </h3>
              <p className="text-ui text-sm md:text-xs mb-5" style={{ color: "#6B7280", fontWeight: 300 }}>
                {assessmentCard.tagline}
              </p>
              <div className="mb-7 mt-2" style={{ borderTop: "1px solid rgba(148, 163, 184, 0.25)" }} />
              <div className="flex flex-wrap gap-2 mb-6">
                {assessmentCard.tags.map((tag) => (
                  <span key={tag} className="text-ui text-[10px] uppercase px-2 py-1 rounded-md" style={{ color: "#0C447C", backgroundColor: "#EAF2FF", fontWeight: 500, letterSpacing: "0.06em" }}>
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-ui optra-description-text text-sm md:text-lg mb-3 leading-relaxed" style={{ color: "#1F2937", fontWeight: 400 }}>
                {assessmentCard.lead}
              </p>
              <p className="text-ui optra-description-text text-sm mb-6 leading-relaxed" style={{ color: "#6B7280", fontWeight: 300 }}>
                {assessmentCard.body}
              </p>
              <ul className="space-y-3 mb-8">
                {assessmentCard.bullets.map((item, idx) => (
                  <li key={idx} className="text-ui text-sm md:text-base flex items-start gap-2" style={{ color: "#4B5563", fontWeight: 300 }}>
                    <span className="mt-0.5 flex-shrink-0" style={{ color: "#60A5FA" }}>✓</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={assessmentCard.cta.href}
                {...(assessmentCard.cta.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="group mt-auto inline-flex items-center justify-center gap-2 w-full text-ui px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  color: "#FFFFFF",
                  background: "#60A5FA",
                  border: "1px solid #60A5FA",
                  cursor: "pointer",
                  fontWeight: 500,
                  boxShadow: "0 8px 20px rgba(96, 165, 250, 0.26)",
                }}
              >
                Start Your Assessment
              </a>
              {typeof window !== "undefined" && !prefersReducedMotion && (
                <>
                  <BorderBeam
                    duration={6}
                    size={400}
                    borderWidth={1}
                    className="from-transparent via-white to-transparent opacity-80"
                  />
                  <BorderBeam
                    duration={6}
                    delay={3}
                    size={400}
                    borderWidth={2}
                    className="from-transparent via-blue-400 to-transparent opacity-60"
                  />
                </>
              )}
            </MotionDiv>

            {/* Card 2: the advisory call. Same everywhere; the outlined secondary of the pair. */}
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-2xl border border-[#DCE8FB] bg-white/92 backdrop-blur-xl shadow-[0_20px_48px_rgba(96,165,250,0.16)] p-5 sm:p-6 md:p-8 flex flex-col min-h-[560px] transition-transform duration-200 hover:-translate-y-0.5"
            >
              <span
                className="inline-flex items-center w-fit text-ui text-[10px] uppercase mb-4 px-2.5 py-1"
                style={{
                  color: "#60A5FA",
                  backgroundColor: "#FFFFFF",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  borderRadius: "8px",
                  border: "1px solid rgba(96, 165, 250, 0.35)",
                  boxShadow: "0 0 0 1px rgba(96, 165, 250, 0.08), 0 4px 12px rgba(96, 165, 250, 0.12)",
                }}
              >
                {supportCard.kicker}
              </span>
              <h3 className="text-emphasis text-[1.59375rem] md:text-[1.8275rem] leading-[1.15] mb-6" style={{ color: "#60A5FA", fontWeight: 400 }}>
                {supportCard.title}
              </h3>
              <p className="text-ui text-sm md:text-xs mb-6" style={{ color: "#6B7280", fontWeight: 300 }}>
                {supportCard.tagline}
              </p>
              <div className="mb-7 mt-2" style={{ borderTop: "1px solid rgba(148, 163, 184, 0.25)" }} />
              <div className="flex flex-wrap gap-2 mb-6">
                {supportCard.tags.map((tag) => (
                  <span key={tag} className="text-ui text-[10px] uppercase px-2 py-1 rounded-md" style={{ color: "#0C447C", backgroundColor: "#EAF2FF", fontWeight: 500, letterSpacing: "0.06em" }}>
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-ui optra-description-text text-sm mb-6 leading-relaxed" style={{ color: "#6B7280", fontWeight: 300 }}>
                {supportCard.body}
              </p>
              <ul className="space-y-3 mb-8">
                {supportCard.bullets.map((item, idx) => (
                  <li key={idx} className="text-ui text-sm md:text-base flex items-start gap-2" style={{ color: "#4B5563", fontWeight: 300 }}>
                    <span className="mt-0.5 flex-shrink-0" style={{ color: "#60A5FA" }}>✓</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </MotionDiv>

          </div>
          )}
          <p
            className="text-center mt-6 text-ui text-sm"
            style={{ color: "#64748B", fontWeight: 300 }}
          >
            Interested in our partners? Contact{" "}
            <a
              href="mailto:sales@optra-labs.com"
              className="text-ui font-medium text-[#60A5FA] hover:underline"
              data-inline-link=""
              style={{ fontWeight: 500, display: "inline" }}
            >
              sales@optra-labs.com
            </a>{" "}
            for an introduction.
          </p>
      </section>

      <section aria-labelledby="comparison-heading" className="section-standard max-w-6xl mx-auto px-4 md:px-6 relative z-20">
        <div className="text-center mb-8 md:mb-12">
        <span
          className="inline-flex items-center text-ui text-[10px] uppercase mb-4 px-2.5 py-1"
          style={{
            color: "#60A5FA",
            backgroundColor: "#FFFFFF",
            fontWeight: 500,
            letterSpacing: "0.08em",
            borderRadius: "8px",
            border: "1px solid rgba(96, 165, 250, 0.35)",
          }}
        >
          Platform comparison
        </span>
        <h2
          id="comparison-heading"
          className="text-emphasis text-[1.7rem] sm:text-[1.59375rem] md:text-[3.1875rem] lg:text-[3.1875rem] leading-[1.08] mb-4"
          style={{ color: "#60A5FA", fontWeight: 400, letterSpacing: "-0.022em" }}
        >
          OPTRA Labs vs Grant Consultants
        </h2>
        <p className="text-ui optra-description-text text-sm sm:text-xl text-center mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed" style={{ color: "#4B5563", fontWeight: 300 }}>
          {home.comparison.intro}
        </p>
        </div>
        <ComparisonTable />
      </section>
      </div>

      {/* FAQ Section - Above Footer */}
      <section id="faq-section" data-faq-section className="section-standard relative z-20 bg-transparent">
        <div className="w-full">
          <FAQAccordion />
        </div>
      </section>

      {/* Enhanced Footer */}
      <EnhancedFooter />

      {mounted && isDemoModalOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              className="fixed inset-0 z-[10000]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="view-demo-modal-title"
            >
              <button
                type="button"
                className="absolute inset-0 cursor-default bg-slate-950/55 backdrop-blur-sm"
                aria-label="Close demo modal"
                onClick={closeDemoModal}
              />

              <div
                ref={demoModalRef}
                className={`absolute left-1/2 top-1/2 z-10 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[8px] border bg-white shadow-2xl ${
                  demoStep === "email" ? "max-w-[34rem]" : "max-w-[58rem]"
                }`}
                style={{
                  borderColor: "rgba(96, 165, 250, 0.18)",
                  background: "#FFFFFF",
                  boxShadow: "0 30px 90px rgba(15, 23, 42, 0.24)",
                }}
              >
                {demoStep === "email" ? (
                  <div
                    className="border-b px-5 py-5 sm:px-7 sm:py-6"
                    style={{ borderColor: "rgba(148, 163, 184, 0.18)" }}
                  >
                    <div>
                      <h3
                        id="view-demo-modal-title"
                        className="text-ui text-sm sm:text-[0.98rem] leading-relaxed"
                        style={{
                          color: "#0F172A",
                          fontWeight: 450,
                          margin: 0,
                          fontFamily:
                            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        }}
                      >
                        View Demo
                      </h3>
                      <p
                        className="text-ui mt-2 text-sm sm:text-[0.98rem] leading-relaxed"
                        style={{ color: "#64748B", fontWeight: 300, margin: 0, maxWidth: "28rem" }}
                      >
                        Enter your email to unlock the OPTRA demo experience.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div aria-hidden="true" />
                )}

                {demoStep === "email" ? (
                  <form onSubmit={handleDemoLeadSubmit} className="px-5 py-6 sm:px-7 sm:py-7">
                    <input
                      id="demo-email-input"
                      type="email"
                      autoComplete="email"
                      value={demoEmail}
                      onChange={(event) => setDemoEmail(event.target.value)}
                      placeholder="you@company.com"
                      className="text-ui w-full rounded-[8px] border px-4 py-3 text-[0.98rem] outline-none focus:outline-none focus:ring-0"
                      style={{
                        color: "#0F172A",
                        backgroundColor: "#FFFFFF",
                        borderColor: demoError
                          ? "rgba(185, 28, 28, 0.24)"
                          : "rgba(148, 163, 184, 0.24)",
                        boxShadow: "none",
                        fontWeight: 300,
                      }}
                    />

                    {demoError ? (
                      <p
                        className="text-ui mt-3 text-sm leading-relaxed"
                        style={{ color: "#B91C1C", fontWeight: 300, marginBottom: 0 }}
                        role="alert"
                      >
                        {demoError}
                      </p>
                    ) : (
                      <p
                        className="text-ui mt-3 text-sm leading-relaxed"
                        style={{ color: "#64748B", fontWeight: 300, marginBottom: 0 }}
                      >
                        The walkthrough unlocks right away. We may follow up about the assessment at this address.
                      </p>
                    )}

                    <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={closeDemoModal}
                        className="text-ui rounded-[8px] border px-5 py-3 text-sm transition-colors"
                        style={{
                          color: "#475569",
                          borderColor: "rgba(148, 163, 184, 0.18)",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          fontWeight: 300,
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={demoSubmitting}
                        className="text-ui rounded-[8px] px-5 py-3 text-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-70"
                        style={{
                          color: "#FFFFFF",
                          backgroundColor: "#60A5FA",
                          border: "none",
                          fontWeight: 400,
                          boxShadow: "0 14px 32px rgba(59, 130, 246, 0.22)",
                        }}
                      >
                        {demoSubmitting ? "Unlocking Demo..." : "Continue to Demo"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="px-5 py-5 sm:px-6 sm:py-6">
                    <div
                      className="overflow-hidden rounded-[8px] bg-slate-950"
                    >
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        className="block w-full h-auto bg-slate-950"
                        src={home.demo.video || DEFAULT_DEMO_VIDEO_SRC}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>

                    <div className="pt-3 sm:pt-4">
                      <p
                        className="text-ui text-sm leading-[1.75]"
                        style={{ color: "#475569", fontWeight: 300, margin: 0 }}
                      >
                        {home.demo.description}
                      </p>
                      <p
                        className="text-ui text-sm leading-[1.75] mt-2"
                        style={{ color: "#475569", fontWeight: 300, margin: 0 }}
                      >
                        {home.demo.note}{" "}
                        {home.demo.legal.inRouter ? (
                          <Link to={home.demo.legal.privacyHref} className="demo-legal-link" data-inline-link="" style={{ display: "inline" }}>Privacy Policy</Link>
                        ) : (
                          <a href={home.demo.legal.privacyHref} className="demo-legal-link" data-inline-link="" style={{ display: "inline" }}>Privacy Policy</a>
                        )}
                        {" and "}
                        {home.demo.legal.inRouter ? (
                          <Link to={home.demo.legal.termsHref} className="demo-legal-link" data-inline-link="" style={{ display: "inline" }}>Terms of Service</Link>
                        ) : (
                          <a href={home.demo.legal.termsHref} className="demo-legal-link" data-inline-link="" style={{ display: "inline" }}>Terms of Service</a>
                        )}
                        .
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
    </MotionConfig>
  );
}
